import { Button } from "@/components/ui/button";
import { Shield, ArrowRight } from "lucide-react";
import { Link, useNavigate, useLocation } from "react-router-dom";

const Hero = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const handleConsentLogsClick = (e: React.MouseEvent) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      // If not on homepage, navigate there first
      navigate('/');
      // Wait for navigation to complete, then scroll to section
      setTimeout(() => {
        const element = document.getElementById('how-solidwraps-works');
        if (element) {
          element.scrollIntoView({ behavior: 'smooth' });
        }
      }, 100);
    } else {
      // If already on homepage, just scroll to section
      const element = document.getElementById('how-solidwraps-works');
      if (element) {
        element.scrollIntoView({ behavior: 'smooth' });
      }
    }
  };

  return (
    <section className="relative pt-32 pb-24 bg-white dark:bg-gray-950 overflow-hidden">
      {/* Clean background with subtle accent */}
      <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-gray-50 to-transparent dark:from-gray-900 dark:to-transparent -z-10" />
      
      <div className="container mx-auto px-6">
        <div className="max-w-6xl mx-auto">
          <div className="grid lg:grid-cols-5 gap-12 items-start">
            {/* Left Column - Main Content (3/5 width) */}
            <div className="lg:col-span-3">
              <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 dark:text-white mb-6 leading-[1.1]">
                Your Terms Don't Matter If You <span className="text-blue-600">Can't Prove</span> They Were Seen
              </h1>

              <p className="text-xl text-gray-600 dark:text-gray-300 mb-8 leading-relaxed">
                Without clickwrap consent proof, your Terms & Conditions are worthless in legal disputes.
                <br />
                <strong className="text-gray-900 dark:text-white">SolidWraps creates <button onClick={handleConsentLogsClick} className="text-blue-600 hover:text-blue-700 underline bg-transparent border-none cursor-pointer">tamper-proof consent logs</button> you can trust in court.</strong>
              </p>

              {/* Single, prominent CTA */}
              <div className="mb-6">
                <Button 
                  size="lg" 
                  className="text-lg px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold shadow-lg"
                  asChild
                >
                  <Link to="/auth?tab=signup">
                    Start Free Trial
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Link>
                </Button>
              </div>

              {/* Trust indicators with keywords */}
              <p className="text-sm text-gray-500 dark:text-gray-400">
                GDPR compliant • Court-ready exports • Works with any platform
              </p>
            </div>

            {/* Right Column - Elegant Before/After (2/5 width) */}
            <div className="lg:col-span-2 md:max-w-md md:mx-auto">
              <div className="relative">
                {/* Background blur effect */}
                <div className="absolute inset-0 bg-gradient-to-br from-blue-50/50 to-indigo-50/50 dark:from-blue-950/20 dark:to-indigo-950/20 rounded-2xl blur-xl"></div>
                
                {/* Main container */}
                <div className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-sm border border-gray-200/50 dark:border-gray-700/50 rounded-2xl p-8 shadow-2xl shadow-blue-500/10">
                  
                  {/* Elegant divider with icon */}
                  <div className="flex items-center justify-center mb-8">
                    <div className="flex items-center gap-4">
                      <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent w-16"></div>
                      <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center">
                        <Shield className="w-4 h-4 text-white" />
                      </div>
                      <div className="h-px bg-gradient-to-r from-transparent via-gray-300 dark:via-gray-600 to-transparent w-16"></div>
                    </div>
                  </div>

                  <div className="space-y-8">
                    {/* Risk items */}
                    {[
                      { risk: '"I never agreed to that"', proof: 'Timestamped consent proof' },
                      { risk: 'No policy version on file', proof: 'Exact version served logged' },
                      { risk: 'Weak evidence in court', proof: 'Legal-grade proof for any dispute' }
                    ].map((item, index) => (
                      <div key={index} className="group">
                        {/* Risk */}
                        <div className="text-gray-500 dark:text-gray-400 text-sm mb-2 group-hover:text-gray-400 dark:group-hover:text-gray-300 transition-colors">
                          {item.risk}
                        </div>
                        {/* Proof */}
                        <div className="text-gray-900 dark:text-white font-semibold group-hover:text-blue-600 dark:group-hover:text-blue-400 transition-colors">
                          {item.proof}
                        </div>
                        {/* Subtle separator */}
                        {index < 2 && (
                          <div className="mt-6 h-px bg-gradient-to-r from-transparent via-gray-200 dark:via-gray-700 to-transparent opacity-50"></div>
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Subtle footer */}
                  <div className="mt-8 pt-6 border-t border-gray-100 dark:border-gray-800">
                    <div className="flex items-center justify-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                      <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                      <span>Real-time protection</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;